import { EventBuss } from '@/types/global'
import { MessageBoxActionType, MessageBoxEvent } from '@/types/MessageBox'
import { Emitter } from 'vue'

export class MJWebsocket {
    constructor(_emitter: Emitter) {
        this.emitter = _emitter
    }

    ws: WebSocket | null = null
    isConnected = false
    isConnecting = false
    emitter!: Emitter

    heartBeatInterval = 15000
    reconnectInterval = 3000
    maxReconnectAttempts = 5
    maxMissedPingCycles = 4

    OnMessage!: (event: MessageEvent) => void

    private url = ''
    private heartBeat: number | boolean = false
    private reconnectTimer: number | boolean = false
    private reconnectCount = 0
    private missedPingCycles = 0
    private closedByClient = false
    private isShutdown = false
    private hasConnectedBefore = false
    private allowReconnect = false

    connect(url: string): void {
        if (this.isShutdown || !url) return

        this.allowReconnect = true

        const sameUrl = url === this.url
        const alive =
            this.isConnected &&
            this.ws?.readyState === WebSocket.OPEN

        if (sameUrl && (this.isConnecting || alive)) {
            return
        }

        this.url = url
        this.reconnectCount = 0
        this.closedByClient = false
        this.clearReconnectTimer()
        this.teardownSocket()
        this.openSocket()
    }

    reconnectIfNeeded(url: string): void {
        if (this.isShutdown || !url || !this.allowReconnect) return
        if (this.isConnecting) return

        const alive =
            this.isConnected &&
            this.ws?.readyState === WebSocket.OPEN &&
            url === this.url

        if (alive) return

        this.connect(url)
    }

    close(): void {
        this.allowReconnect = false
        this.closedByClient = true
        this.clearReconnectTimer()
        this.teardownSocket()
        this.url = ''
        this.closedByClient = false
        this.hasConnectedBefore = false
    }

    shutdown(): void {
        this.isShutdown = true
        this.allowReconnect = false
        this.closedByClient = true
        this.clearReconnectTimer()
        this.teardownSocket()
        this.url = ''
        this.closedByClient = false
        this.hasConnectedBefore = false
    }

    showMessage(): void {
        const evt: MessageBoxEvent = {
            type: MessageBoxActionType.open,
            name: 'message'
        }
        this.emitter.emit(EventBuss.DIALOG, evt)
        setTimeout(() => {
            this.emitter.emit(
                EventBuss.HINT,
                '因网络波动异常，请您重新登录系统，谢谢！'
            )
        }, 250)
    }

    private openSocket(): void {
        if (this.isShutdown || !this.url) return
        if (this.isConnecting || this.isConnected || this.ws) return

        this.isConnecting = true
        console.log('connecting to ws')

        const socket = new WebSocket(this.url)
        this.ws = socket

        socket.onopen = () => {
            if (this.ws !== socket) return

            this.isConnecting = false
            this.isConnected = true
            this.reconnectCount = 0
            this.missedPingCycles = 0
            console.log('connected')
            this.startHeartBeat()

            if (this.hasConnectedBefore) {
                this.emitter.emit(EventBuss.SOCKET_RECONNECTED)
            } else {
                this.hasConnectedBefore = true
            }
        }

        socket.onmessage = (event: MessageEvent) => {
            if (this.ws !== socket) return

            this.missedPingCycles = 0
            if (typeof this.OnMessage === 'function') {
                this.OnMessage(event)
            }
        }

        socket.onclose = () => {
            if (this.ws !== socket) return

            this.isConnecting = false
            this.isConnected = false
            this.stopHeartBeat(false)
            this.ws = null

            if (this.closedByClient || this.isShutdown || !this.allowReconnect) {
                return
            }

            this.scheduleReconnect()
        }

        socket.onerror = (event: Event) => {
            console.log(event)
            console.log('ws error')
        }
    }

    private startHeartBeat(): void {
        this.stopHeartBeat(false)
        this.heartBeat = window.setInterval(() => {
            if (!this.isConnected || !this.ws) {
                this.stopHeartBeat(false)
                return
            }

            if (this.ws.readyState === WebSocket.OPEN) {
                try {
                    this.ws.send(
                        JSON.stringify({ type: 'client-ping', data: {} })
                    )
                } catch (error) {
                    console.log('ws ping failed', error)
                }
            }

            this.missedPingCycles++
            if (this.missedPingCycles > this.maxMissedPingCycles) {
                console.log('ws heartbeat timeout')
                this.teardownSocket()
                if (this.allowReconnect) {
                    this.scheduleReconnect()
                }
            }
        }, this.heartBeatInterval)
    }

    private scheduleReconnect(): void {
        if (
            this.isShutdown ||
            !this.url ||
            !this.allowReconnect ||
            this.reconnectTimer !== false
        ) {
            return
        }

        if (this.reconnectCount >= this.maxReconnectAttempts) {
            this.reconnectCount = 0
            this.showMessage()
            return
        }

        this.reconnectTimer = window.setTimeout(() => {
            this.reconnectTimer = false
            if (
                this.isShutdown ||
                this.closedByClient ||
                !this.allowReconnect
            ) {
                return
            }

            console.log('reconnect ws')
            this.reconnectCount++
            this.openSocket()
        }, this.reconnectInterval)
    }

    private teardownSocket(): void {
        this.stopHeartBeat(false)
        this.isConnecting = false
        this.isConnected = false

        const socket = this.ws
        if (!socket) return

        this.ws = null
        socket.onopen = null
        socket.onmessage = null
        socket.onerror = null
        socket.onclose = null

        if (
            socket.readyState === WebSocket.OPEN ||
            socket.readyState === WebSocket.CONNECTING
        ) {
            socket.close()
        }
    }

    private clearReconnectTimer(): void {
        if (typeof this.reconnectTimer === 'number') {
            clearTimeout(this.reconnectTimer)
            this.reconnectTimer = false
        }
    }

    private stopHeartBeat(logStop: boolean): void {
        if (typeof this.heartBeat === 'number') {
            clearInterval(this.heartBeat)
            this.heartBeat = false
            if (logStop) {
                console.log('ws heartbeat stoped')
            }
        }
    }
}
